import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KachelLink } from "@/components/KachelLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Landmark, Hash, ExternalLink, Pencil, Upload, LayoutDashboard, GripVertical } from "lucide-react";
import { listEntities, createEntity, updateEntity, deleteEntity, getCockpitStats } from "@/lib/api";
import { LEGAL_FORMS, UNIT_TYPES } from "@/lib/format";
import { useEntityOrder } from "@/lib/useEntityOrder";
import { useSchreibschutz } from "@/lib/auth";
import { sperre } from "@/hilfe/Knopfhilfe";
import { toast } from "sonner";
import BestandImportDialog from "@/components/BestandImportDialog";
import DeleteConfirm from "@/components/DeleteConfirm";
import { SearchBox, useFulltextFilter, MatchCount } from "@/components/SearchBox";
import SuchAuswahl from "@/components/ui/such-auswahl";

const typLabel = (v) => UNIT_TYPES.find((t) => t.value === v)?.label || v;

const empty = {
    name: "", rechtsform: "privat", steuernummer: "", iban: "",
    briefkopf_vorlage: "", kontakt_name: "", kontakt_email: "", notes: "",
};

const Entities = () => {
    const [items, setItems] = useState([]);
    const [cockpit, setCockpit] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [importFor, setImportFor] = useState(null);
    const { gesperrt, grund } = useSchreibschutz();

    const load = async () => {
        const [list, c] = await Promise.all([listEntities(), getCockpitStats()]);
        setItems(list);
        setCockpit(c);
    };
    useEffect(() => { load(); }, []);

    const startCreate = () => {
        setEditingId(null);
        setForm(empty);
        setOpen(true);
    };

    const startEdit = (e) => {
        setEditingId(e.id);
        setForm({
            // Version mitführen, sonst schickt die API-Schicht kein If-Match und
            // der Schreibkonflikt bleibt unentdeckt (die anderen Seiten spreaden
            // den Datensatz und bekommen sie automatisch).
            version: e.version,
            name: e.name ?? "",
            rechtsform: e.rechtsform ?? "privat",
            steuernummer: e.steuernummer ?? "",
            iban: e.iban ?? "",
            briefkopf_vorlage: e.briefkopf_vorlage ?? "",
            kontakt_name: e.kontakt_name ?? "",
            kontakt_email: e.kontakt_email ?? "",
            notes: e.notes ?? "",
        });
        setOpen(true);
    };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name ist erforderlich"); return; }
        setSaving(true);
        try {
            if (editingId) {
                await updateEntity(editingId, form);
                toast.success("Entität aktualisiert");
            } else {
                await createEntity(form);
                toast.success("Entität angelegt");
            }
            setOpen(false); setForm(empty); setEditingId(null); load();
        } finally { setSaving(false); }
    };

    const statsFor = (id) => cockpit?.per_entity?.find((e) => e.entity_id === id);

    // Volltextsuche
    const { query, setQuery, filtered: searched, total, matches } = useFulltextFilter(items, [
        "name", "rechtsform", "steuernummer", "iban", "kontakt_name", "kontakt_email", "notes",
    ]);

    // Drag & Drop Reorder (gleicher localStorage-Key wie Cockpit)
    const { ordered, handlers, reset, isReordered, dragId, overId } = useEntityOrder(searched, "id");

    return (
        <div className="px-6 md:px-10 py-8 space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Eigentümer / Mandanten</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="entities-title">Entitäten</h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Steuerlich eigenständige Eigentümer: Privatpersonen, GbR, Genossenschaften, Stiftungen. Jede Immobilie gehört zu genau einer Entität.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={startCreate} data-testid="btn-new-entity"
                                disabled={gesperrt} grund={grund}>
                            <Plus size={14} className="mr-1.5" /> Neue Entität
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl">
                        <DialogHeader><DialogTitle>{editingId ? "Entität bearbeiten" : "Neue Entität anlegen"}</DialogTitle></DialogHeader>
                        <div className="grid grid-cols-2 gap-3 py-2">
                            <div className="col-span-2">
                                <Label>Name *</Label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="z.B. Schäfer/Klötzel Familienstiftung" data-testid="input-entity-name" />
                            </div>
                            <div>
                                <Label>Rechtsform</Label>
                                <SuchAuswahl wert={form.rechtsform} onWaehlen={(v) => setForm({ ...form, rechtsform: v })}
                                    optionen={[
                                        ...LEGAL_FORMS.map((f) => ({ wert: f, text: f })),
                                    ]}
                                    testId="input-entity-form"
                                />
                            </div>
                            <div>
                                <Label>Steuernummer</Label>
                                <Input value={form.steuernummer} onChange={(e) => setForm({ ...form, steuernummer: e.target.value })} data-testid="input-entity-stnr" />
                            </div>
                            <div className="col-span-2">
                                <Label>IBAN (Mieteingänge)</Label>
                                <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="DE00 0000 0000 0000 0000 00" data-testid="input-entity-iban" />
                            </div>
                            <div>
                                <Label>Ansprechpartner</Label>
                                <Input value={form.kontakt_name} onChange={(e) => setForm({ ...form, kontakt_name: e.target.value })} />
                            </div>
                            <div>
                                <Label>E-Mail</Label>
                                <Input value={form.kontakt_email} onChange={(e) => setForm({ ...form, kontakt_email: e.target.value })} />
                            </div>
                            <div className="col-span-2">
                                <Label>Briefkopf-Vorlage (URL zur PDF)</Label>
                                <Input value={form.briefkopf_vorlage} onChange={(e) => setForm({ ...form, briefkopf_vorlage: e.target.value })} placeholder="https://drive.google.com/..." />
                            </div>
                            <div className="col-span-2">
                                <Label>Notizen</Label>
                                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
                            <Button onClick={save} disabled={saving || gesperrt} grund={sperre([saving, "Wird gespeichert"], [gesperrt, grund])} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-entity">
                                {saving ? "Speichere…" : (editingId ? "Aktualisieren" : "Speichern")}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filterleiste */}
            <div className="bg-white border border-gray-200 rounded-md p-3 flex items-center gap-3">
                <SearchBox value={query} onChange={setQuery} placeholder="Suche Name, Steuernummer, IBAN, Kontakt…" testid="entities-search" className="w-[340px]" />
                <MatchCount matches={matches} total={total} label="Entitäten" />
                {ordered.length > 1 && (
                    <div className="ml-auto flex items-center gap-3 text-xs text-gray-500">
                        <span>Reihenfolge per Drag &amp; Drop anpassen</span>
                        {isReordered && (
                            <button type="button" onClick={reset} className="text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline" data-testid="reset-entity-order">
                                Zurücksetzen
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ordered.map((e) => {
                    const s = statsFor(e.id);
                    const isDragging = dragId === e.id;
                    const isOver = overId === e.id && dragId && dragId !== e.id;
                    return (
                        <div
                            key={e.id}
                            {...handlers(e)}
                            className={`bg-white border rounded-md p-5 shadow-flat transition-all cursor-grab active:cursor-grabbing
                                ${isDragging ? "opacity-40 border-slate-400" : "border-gray-200 hover:border-slate-400"}
                                ${isOver ? "ring-2 ring-slate-400 border-slate-400" : ""}`}
                            data-testid={`entity-card-${e.id}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex items-start gap-2">
                                    <GripVertical size={14} className="text-gray-300 mt-0.5 shrink-0" />
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <Landmark size={14} className="text-gray-400" />
                                            <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">{e.rechtsform || "—"}</span>
                                        </div>
                                        <div className="font-heading font-semibold text-slate-900 mt-1 truncate">{e.name}</div>
                                    </div>
                                </div>
                            </div>
                            {/* Kleinere Laufweite fuer diese drei Beschriftungen:
                                "KALTMIETE/MO." hat kein Leerzeichen, kann also nicht
                                umbrechen, und stand mit der grossen Sperrung 15 px
                                ueber seiner Spalte. */}
                            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-3 text-center">
                                <KachelLink href={`/immobilien?entity_id=${e.id}`}
                                            title={`Die ${s?.properties ?? 0} Immobilien dieser Entität`}>
                                    <div className="kachel-label uppercase text-gray-400">Immobilien</div>
                                    <div className="text-lg font-bold text-slate-900 tabular-nums">{s?.properties ?? 0}</div>
                                </KachelLink>
                                <KachelLink href={`/einheiten?entity_id=${e.id}`}
                                            title={`Die ${s?.units ?? 0} Einheiten dieser Entität`}>
                                    <div className="kachel-label uppercase text-gray-400">Einheiten</div>
                                    <div className="text-lg font-bold text-slate-900 tabular-nums">{s?.units ?? 0}</div>
                                </KachelLink>
                                <KachelLink href={`/entitaeten/${e.id}`} title="Kaltmiete pro Monat — zum Cockpit">
                                    <div className="kachel-label uppercase text-gray-400">Kaltmiete/Mo.</div>
                                    <div className="text-base xl:text-lg font-bold text-slate-900 tabular-nums">{s ? Math.round(s.monat_kalt) + " €" : "—"}</div>
                                </KachelLink>
                            </div>
                            {s?.by_typ && Object.keys(s.by_typ).length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-100 space-y-1"
                                     data-testid={`entity-typen-${e.id}`}>
                                    {Object.entries(s.by_typ)
                                        .filter(([, n]) => n > 0)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([k, n]) => (
                                            <KachelLink key={k} href={`/einheiten?entity_id=${e.id}&typ=${k}`}
                                                        title={`${n} × ${typLabel(k)} in dieser Entität`}
                                                        className="flex items-baseline justify-between gap-2 text-[11px] group">
                                                <span className="text-gray-500 truncate group-hover:text-slate-900">{typLabel(k)}</span>
                                                <span className="text-slate-700 font-medium tabular-nums shrink-0">{n}</span>
                                            </KachelLink>
                                        ))}
                                </div>
                            )}
                            <div className="mt-4 space-y-1.5 text-xs text-gray-500">
                                {e.steuernummer && <div className="flex items-center gap-1"><Hash size={10} /> {e.steuernummer}</div>}
                                {e.iban && <div className="font-mono text-[11px] truncate">{e.iban}</div>}
                                {e.briefkopf_vorlage && <a href={e.briefkopf_vorlage} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"><ExternalLink size={10} /> Briefkopf-Vorlage</a>}
                            </div>
                            {/* flex-wrap: fuenf Schaltflaechen passen in eine
                                dreispaltige Kachel nicht nebeneinander. Ohne
                                Umbruch standen sie am 10.09.2026 um bis zu 80 px
                                ausserhalb der Karte. */}
                            <div
                                className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2"
                                draggable={false}
                                onMouseDown={(ev) => ev.stopPropagation()}
                                onDragStart={(ev) => ev.preventDefault()}
                            >
                                <Link to={`/entitaeten/${e.id}`} data-testid={`view-entity-cockpit-${e.id}`}
                                      className="flex-1 min-w-[7rem]">
                                    <Button variant="default" size="sm" className="w-full h-8 bg-slate-900 hover:bg-slate-800">
                                        <LayoutDashboard size={12} className="mr-1.5" /> Cockpit
                                    </Button>
                                </Link>
                                <Link to={`/immobilien?entity_id=${e.id}`} data-testid={`view-entity-props-${e.id}`}>
                                    <Button variant="outline" size="sm" className="h-8">Immobilien</Button>
                                </Link>
                                <Button variant="ghost" size="sm" className="h-8" onClick={() => setImportFor(e)} data-testid={`import-entity-${e.id}`}
                                        disabled={gesperrt} grund={grund}
                                        hilfe="aktion.importBestand" aria-label="Bestand importieren">
                                    <Upload size={12} />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-8" onClick={() => startEdit(e)} data-testid={`edit-entity-${e.id}`}
                                        disabled={gesperrt} grund={grund}>
                                    <Pencil size={12} />
                                </Button>
                                <DeleteConfirm
                                    label={e.name}
                                    kind="entity"
                                    onDelete={(cascade) => deleteEntity(e.id, cascade)}
                                    onDeleted={load}
                                    testid={`delete-entity-${e.id}`}
                                    disabled={gesperrt}
                                    grund={grund}
                                />
                            </div>
                        </div>
                    );
                })}
                {ordered.length === 0 && (
                    <div className="col-span-full py-16 text-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-md">
                        {items.length === 0
                            ? "Noch keine Entitäten — bitte anlegen oder Demo-Daten im Cockpit laden."
                            : "Keine Entitäten passen zur Suche."}
                    </div>
                )}
            </div>

            <BestandImportDialog
                open={!!importFor}
                onOpenChange={(v) => !v && setImportFor(null)}
                entity={importFor}
                onImported={() => { load(); setImportFor(null); }}
            />
        </div>
    );
};

export default Entities;
